<template>
  <CustomDrawer
    v-model:modelValue="internalDrawerVisible"
    direction="right"
    width="600px"
    :title="isEditing ? 'Edit Meeting Agent' : 'Create New Meeting Agent'"
  >
    <template #subtitle>
      <span class="secondary_color proxima_nova font-normal text-sm">
        {{ agentType }}
      </span>
    </template>
    <div
      class="create_agent_form flex flex-col gap-6 text-[13px] secondary_color proxima_nova font-normal"
    >
      <div id="agent_name" class="flex flex-col gap-1">
        <div class="form_col">
          <span>Agent Name</span>
          <CustomInput
            format="text"
            v-model="agentForm.agent_name"
            placeholder="Enter Name"
            type="primary"
            :is-error="websiteError !== '' || isNameTooLong"
            :error-message="websiteError"
            :on-change="() => (websiteError = '')"
            maxlength="50"
          />
        </div>
        <div
          :class="[
            'text-xs ml-1',
            agentNameLength >= 50 ? 'error_message_color' : 'secondary_color',
          ]"
        >
          {{ agentNameLength }}/50 characters
        </div>
      </div>

      <div v-if="props.agentType === 'Round Robin'" id="team" class="form_col">
        <span>Select Team</span>
        <a-select
          v-model:value="agentForm.team_id"
          placeholder="Select team"
          :maxTagCount="3"
          @change="handleTeamChange"
          allow-clear
        >
          <template v-if="!teamOptions?.length">
            <a-select-option disabled>
              <div class="flex flex-col gap-2 py-2">
                <span class="secondary_color">No team available</span>
                <a
                  class="text-gray-500 hover:secondary_color cursor-pointer flex items-center gap-1"
                  @click="goToTeams"
                >
                  + Create new team
                </a>
              </div>
            </a-select-option>
          </template>
          <template v-else>
            <a-select-option
              v-for="member in teamOptions"
              :key="member.id"
              :value="member.id"
            >
              {{ member.name }}
            </a-select-option>
          </template>
        </a-select>
      </div>

      <div id="duration" class="form_col">
        <span>Available durations</span>
        <a-select
          v-if="!allowCustomDuration"
          v-model:value="agentForm.duration"
          placeholder="Set Duration"
          :maxTagCount="3"
          :defaultValue="[30]"
        >
          <template #suffixIcon
            ><ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_white_arrow"
              class="size-4"
          /></template>
          <a-select-option
            v-for="(item, index) in durationOptions"
            :key="`${item.value}_duration_${index}`"
            :value="item.value"
          >
            {{ item.label }}
          </a-select-option>
        </a-select>

        <a-select
          v-else
          v-model:value="sortedDurationOptions"
          placeholder="Set Duration"
          mode="multiple"
          :maxTagCount="4"
          :defaultValue="[30]"
        >
          <template #suffixIcon
            ><ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_white_arrow"
              class="size-4"
          /></template>
          <a-select-option
            v-for="(item, index) in durationOptions"
            :key="`${item.value}_duration_${index}`"
            :value="item.value"
          >
            {{ item.label }}
          </a-select-option>
        </a-select>

        <div class="flex items-center mt-3 gap-2">
          <a-switch v-model:checked="allowCustomDuration" size="small">
          </a-switch>
          <span class="text_color text-sm font-semibold"
            >Allow booker to select duration</span
          >
        </div>
      </div>

      <div v-if="allowCustomDuration" class="form_col">
        <span>Default duration</span>
        <a-select
          v-model:value="agentForm.duration"
          placeholder="Set Default Duration"
        >
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_white_arrow"
              class="size-4"
            />
          </template>
          <a-select-option
            v-for="(item, index) in filteredDurationOptions"
            :key="`${item.value}_duration_${index}`"
            :value="item.value"
          >
            {{ item.label }}
          </a-select-option>
        </a-select>
      </div>

      <div id="theme" class="flex flex-col gap-2">
        <span>Select Theme</span>
        <a-select v-model:value="agentForm.theme">
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_white_arrow"
              class="size-4"
            />
          </template>
          <a-select-option
            v-for="option in themeOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </a-select-option>
        </a-select>
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <a-checkbox v-model:checked="enableBrandColor" class="text_color">
            Custom brand color
          </a-checkbox>
        </div>
        <div
          v-if="enableBrandColor"
          id="brand_color"
          class="flex flex-col gap-2 mt-2"
        >
          <div class="text-sm">
            Brand Color
            {{ agentForm.theme === "dark" ? "(Dark Theme)" : "(Light Theme)" }}
          </div>
          <CustomColorPicker v-model="agentForm.brand_color" />
          <div
            v-if="isLowContrast"
            class="flex gap-2 items-start mt-1 p-3 rounded-lg border-l-4 border-l-amber-500 bg-amber-100/80 dark:bg-amber-900/20"
          >
            <ThemeImage
              :icon="imageNames.WARNING"
              alt="warning"
              class="w-5 h-5 mt-0.5"
            />
            <div class="text-sm font-medium text-amber-900 dark:text-amber-200">
              {{ agentForm.theme === "dark" ? "Dark" : "Light" }} Theme color
              doesn't pass contrast check. We recommend you change this colour
              so your buttons will be more visible.
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="props.agentType === 'Round Robin'"
        id="distribution"
        class="flex flex-col gap-2"
      >
        <div class="flex flex-row items-center gap-1">
          <span>Distribution</span>
          <a-tooltip placement="right">
            <template #title
              ><span class="text-[13px]"
                >Allows for optimising distribution for maximum availability or
                to aim for a more balanced assignment</span
              ></template
            >
            <ThemeImage
              :icon="imageNames.INFO_GREY"
              alt="info_icon"
              class="cursor-pointer"
            />
          </a-tooltip>
        </div>
        <a-select
          v-model:value="agentForm.distribution"
          :defaultValue="'flexible'"
        >
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_white_arrow"
              class="size-4"
            />
          </template>
          <a-select-option
            v-for="option in distributionOptions"
            :key="option.value"
            :value="option.value"
          >
            <div class="flex items-start gap-2">
              <ThemeImage
                class="size-4 mt-1.5"
                :icon="option.icon"
                :alt="option.label"
              />
              <div class="flex flex-col">
                <span class="text_color distribution-label">{{
                  option.label
                }}</span>
                <span
                  class="secondary_color text-sm whitespace-normal distribution-desc"
                  >{{ option.description }}</span
                >
              </div>
            </div>
          </a-select-option>
        </a-select>

        <div class="flex items-center mt-2 gap-2">
          <a-switch v-model:checked="allowSetWeight" size="small"> </a-switch>
          <span class="text_color text-sm font-semibold">Set Weight</span>
          <a-tooltip placement="right">
            <template #title
              ><span class="text-[13px]"
                >Weights determine how meetings are distributed among
                hosts</span
              ></template
            >
            <ThemeImage
              :icon="imageNames.INFO"
              alt="info_icon"
              class="cursor-pointer size-4"
            />
          </a-tooltip>
        </div>

        <div v-if="allowSetWeight" class="mt-2">
          <div class="teams-container rounded-lg">
            <div class="m-3">
              <div class="flex justify-between items-center mb-2">
                <h3 class="text_color text-sm font-semibold">
                  {{ agentForm.team_name }}
                </h3>
              </div>
              <div class="flex flex-col team-box rounded-lg">
                <div
                  v-for="member in teamUsers"
                  :key="member.id"
                  class="flex items-center justify-between py-4 border-b border-[rgba(255,255,255,0.08)] first:pt-1 last:pb-1 last:border-b-0"
                >
                  <div class="flex items-center gap-3">
                    <ProfileIcon
                      :data="member"
                      :param="'name'"
                      :view="true"
                      :size="28"
                      :font="12"
                    />
                    <span
                      class="text_color text-sm items-center justify-center"
                    >
                      {{ member.name }}
                    </span>
                  </div>

                  <div class="flex items-center gap-1">
                    <button
                      class="w-8 h-8 rounded-full flex items-center justify-center text_color"
                      @click="() => decrementWeight(member.id)"
                      :disabled="member.weight <= 1"
                    >
                      <ThemeImage :icon="imageNames.MINUS" alt="minus_icon" />
                    </button>
                    <div
                      class="w-8 h-8 rounded-lg px-5 py-2 secondary_medium flex items-center justify-center text_color"
                    >
                      {{ member.weight }}
                    </div>
                    <button
                      class="w-8 h-8 rounded-full flex items-center justify-center text_color"
                      @click="() => incrementWeight(member.id)"
                      :disabled="member.weight >= 5"
                    >
                      <ThemeImage
                        :icon="imageNames.PLUS"
                        alt="plus_icon"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="props.agentType === 'One-on-One'"
        id="availability"
        class="form_col"
      >
        <span>Availability</span>
        <div class="flex flex-col gap-6">
          <div
            v-for="(day, index) in daysOfWeeks"
            :key="`${day}_day_${index}`"
            class="flex flex-row gap-2 items-start"
          >
            <div class="flex flex-row gap-2 items-center w-40">
              <a-checkbox
                :checked="!agentForm.time_slots[day].unavailable"
                @change="(e) => toggleDayAvailability(day, e.target.checked)"
              />
              <span class="text_color">{{ day }}</span>
            </div>
            <span
              v-if="agentForm.time_slots[day].unavailable"
              class="secondary_color text-sm"
              >Not available</span
            >
            <div v-else class="flex flex-col gap-2">
              <div
                v-for="(slot, index) in agentForm.time_slots[day].slots"
                :key="`${day}_slot_${index}`"
                class="flex flex-row gap-2 items-center"
              >
                <a-select
                  v-model:value="slot.start"
                  showSearch
                  class="w-[112px] cursor-pointer"
                >
                  <template #suffixIcon>
                    <ThemeImage
                      :icon="imageNames.BOTTOM_ARROW"
                      alt="bottom_white_arrow"
                      class="size-4"
                    />
                  </template>
                  <a-select-option
                    v-for="(time, index) in timeIntervals"
                    :key="`${time}_time_${index}`"
                    :value="time"
                  >
                  </a-select-option>
                </a-select>
                <a-select
                  v-model:value="slot.end"
                  showSearch
                  class="w-[112px] cursor-pointer"
                >
                  <template #suffixIcon>
                    <ThemeImage
                      :icon="imageNames.BOTTOM_ARROW"
                      alt="bottom_white_arrow"
                      class="size-4"
                    />
                  </template>
                  <a-select-option
                    v-for="(time, index) in timeIntervals"
                    :key="`${time}_time_${index}`"
                    :value="time"
                  >
                  </a-select-option>
                </a-select>
                <div class="flex flex-row items-center mx-1">
                  <div
                    class="cursor-pointer m-1 p-1"
                    @click="() => handleRemoveSlot(day, index)"
                  >
                    <ThemeImage
                      :icon="imageNames.CLOSE_GREY"
                      alt="cross_icon"
                      class="size-[14px]"
                    />
                  </div>
                  <div
                    v-if="index === 0"
                    class="cursor-pointer mx-2 my-1 p-1"
                    @click="() => handleAddSlot(day)"
                  >
                    <ThemeImage
                      :icon="imageNames.ADD_GREY"
                      alt="cross_icon"
                      class="size-[14px]"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="props.agentType === 'One-on-One'"
        id="timezone"
        class="form_col"
      >
        <span>Timezone</span>
        <a-select
          v-model:value="agentForm.timezone"
          showSearch
          :show-arrow="false"
          :filter-option="filterDropdown"
        >
          <template #suffixIcon
            ><ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_white_arrow"
              class="size-4"
          /></template>
          <a-select-option
            v-for="timezone in timeZones"
            :key="`timezone_${timezone}`"
            :value="timezone"
            :label="timezone.replace('_', ' ')"
          >
            {{ timezone.replace("_", " ") }}
          </a-select-option>
        </a-select>
      </div>
      <div class="gap-2">
        <span>Description</span>
        <div
          id="description"
          class="flex font-normal text-sm mail_reply_container mt-2"
        >
          <QuillEditor
            v-model="agentForm.description"
            class="w-full text_color"
            ref="descriptionEditorRef"
            :height="200"
            @update:modelValue="handleEditorUpdate"
            :customToolbar="[
              ['bold', 'italic', 'underline'],
              [{ list: 'ordered' }, { list: 'bullet' }],
            ]"
          />
          <div
            :class="[
              'text-xs m-1',
              descriptionLength >= 400
                ? 'text-red-500'
                : showDescriptionWarning
                  ? 'text-yellow-500'
                  : 'secondary_color',
            ]"
          >
            {{ descriptionLength }}/{{ DESCRIPTION_LIMITS.PLAIN_TEXT }}
            characters
          </div>
        </div>
      </div>

      <div id="location" class="form_col gap-2">
        <span>Location</span>
        <a-select
          v-model:value="agentForm.meeting_vendor"
          placeholder="Select meeting location"
          show-search
          allow-clear
          @select="handleLocationSelect"
        >
          <a-select-option
            v-for="option in locationOptions"
            :key="option.value"
            :value="option.value"
            :disabled="option.disabled"
          >
            <a-tooltip
              v-if="option.disabled && option.tooltip"
              :title="option.tooltip"
              placement="right"
            >
              <div class="flex items-center gap-2 opacity-50">
                <ThemeImage :icon="option.icon" :alt="option.label" class="w-5 h-5" />
                <span>{{ option.label }}</span>
              </div>
            </a-tooltip>
            <div v-else class="flex items-center gap-2">
              <ThemeImage :icon="option.icon" :alt="option.label" class="w-5 h-5" />
              <span>{{ option.label }}</span>
            </div>
          </a-select-option>
        </a-select>
      </div>

      <div
        v-if="agentForm.meeting_vendor === 'ai_supermeet'"
        id="digital_employee"
        class="form_col gap-2"
      >
        <span>Digital Employee</span>
        <a-select
          v-model:value="agentForm.digital_employee_id"
          placeholder="Select Digital Employee"
          show-search
          :filter-option="filterDropdown"
          :loading="digitalEmployeesLoading"
        >
          <a-select-option
            v-for="emp in digitalEmployees"
            :key="emp.id"
            :value="emp.id"
            :label="emp.name"
          >
            {{ emp.name }}
          </a-select-option>
        </a-select>
      </div>

      <div id="advanced_settings" class="flex flex-col gap-2">
        <AdvancedSettings
          :key="props.agentId || 'new-agent'"
          :initial-settings="advancedSettings"
          @update:advancedSettings="(val) => (advancedSettings = val)"
        />
      </div>
    </div>
    <template #footer>
      <div class="flex flex-row gap-2 justify-end">
        <a-button class="primary_medium" @click="onSubmit" :loading="loading">
          {{ isEditing ? "Update Agent" : "Create Agent" }}
        </a-button>
      </div>
    </template>
  </CustomDrawer>
</template>

<script setup>
import { onMounted, computed, reactive, ref, watch } from "vue";
import CustomDrawer from "@/src/components/CustomDrawer.vue";
import {
  convertTo12Hour,
  convertTo12HourFormat,
  convertTo24Hour,
  convertTo24HourFormat,
  deepCopy,
  filterDropdown,
  formatDuration,
  generateTimeOptions,
  getTimeZone,
} from "@/src/utils/utils.js";
import DashboardService from "@/src/api/DashboardService.js";
import DigitalEmployeeService from "@/src/api/DigitalEmployeeService.js";
import { message } from "ant-design-vue";
import {
  daysOfWeeks,
  defaultTimeSlots,
  durationOptions,
  locationOptionsMap,
} from "@/src/constants/meetingAgentConstants.js";
import CustomInput from "@/src/components/CustomInput.vue";
import { timeZones } from "@/src/constants/timeZoneConstants.js";
import QuillEditor from "@/src/components/quill_editor/QuillEditor.vue";
import {
  checkTextLength,
  DESCRIPTION_LIMITS,
} from "@/src/utils/markdownUtils.js";
import { useRouter } from "vue-router";
import CustomColorPicker from "@/src/components/custom_components/CustomColorPicker.vue";
import AdvancedSettings from "@/src/components/meeting_agent_components/AdvancedSettings.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import { imageNames } from "@/src/constants/imageConstants.js";

const emit = defineEmits([
  "agent-created",
  "agent-updated",
  "update:modelValue",
]);
const props = defineProps({
  agentId: { type: Number, default: null },
  drawerVisible: { type: Boolean, required: true },
  isEditing: { type: Boolean, default: false },
  agentType: { type: String, default: "One-on-One" },
});

const internalDrawerVisible = ref(props.drawerVisible);
const loading = ref(false);
const timeIntervals = generateTimeOptions();
const descriptionEditorRef = ref(null);
const domParser = ref(null);
const allowCustomDuration = ref(false);
const descriptionLength = ref(0);
const showDescriptionWarning = ref(false);
const hasShownExceedMessage = ref(false);
const teamOptions = ref();
const advancedSettings = ref(null);
const allowSetWeight = ref(false);
const digitalEmployees = ref([]);
const digitalEmployeesLoading = ref(false);

const themeOptions = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

const distributionOptions = [
  {
    value: "flexible",
    label: "Flexible",
    description:
      "Allows bookers to schedule with any available host, prioritizing more bookings over even distribution.",
    icon: imageNames.CALENDAR_TICK_GREY,
  },
  {
    value: "strict",
    label: "Strict",
    description:
      "Tracks bookings per host and disables those far ahead to maintain even distribution.",
    icon: imageNames.LOAD_BALANCING_GREY,
  },
];

const getInitials = (name) => {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase();
};
const teamUsers = ref([]);

const incrementWeight = (memberId) => {
  const member = teamUsers.value.find((m) => m.id === memberId);
  if (member && member.weight < 5) {
    member.weight++;
    agentForm.weights[memberId] = member.weight;
  }
};

const decrementWeight = (memberId) => {
  const member = teamUsers.value.find((m) => m.id === memberId);
  if (member && member.weight > 1) {
    member.weight--;
    agentForm.weights[memberId] = member.weight;
  }
};

const router = useRouter();

const goToTeams = () => {
  router.push({
    path: "/settings",
    hash: "#users",
  });
  localStorage.setItem("settingsActiveKey", "1");
};

const createInitialFormState = () => ({
  id: null,
  agent_name: "",
  duration_options: [30],
  time_slots: {},
  description: "",
  timezone: getTimeZone(),
  duration: 30,
  theme: "light",
  brand_color: "",
  type: "",
  team_id: undefined,
  team_name: "",
  meeting_vendor: "super_meet",
  digital_employee_id: undefined,
  distribution: "flexible",
  weight_distribution: "default",
  weights: {},
});
const websiteError = ref("");
const agentForm = reactive(createInitialFormState());
const agentNameLength = ref(0);
const hasShownNameError = ref(false);

const isNameTooLong = computed(() => {
  return agentForm.agent_name.length > 50;
});

const enableBrandColor = ref(false);

watch(enableBrandColor, (newValue) => {
  if (!newValue) {
    agentForm.brand_color = "";
  }
});

const locationOptions = ref([]);

const handleTeamChange = async (value) => {
  if (value) {
    const verifyTeamId = {
      team_id: value,
    };
    try {
      const response = await DashboardService.verifyTeam(verifyTeamId);
      if (response.error) {
        if (Array.isArray(response.error)) {
          response.error.forEach((errorMsg) => {
            message.error(errorMsg);
          });
        } else {
          message.error(response.error);
        }
        agentForm.team_id = undefined;
        agentForm.team_name = "";
        teamUsers.value = [];
      } else if (!response.valid_team) {
        agentForm.team_id = undefined;
        agentForm.team_name = "";
        teamUsers.value = [];
      } else {
        teamUsers.value = response.team_users.map((user) => ({
          ...user,
          weight: 1,
        }));
        agentForm.weights = response.team_users.reduce((acc, user) => {
          acc[user.id] = 1;
          return acc;
        }, {});
        const selectedTeam = teamOptions.value.find(
          (team) => team.id === value,
        );
        if (selectedTeam) {
          agentForm.team_name = selectedTeam.name;
        }
        // Fetch location options after team is selected
        fetchLocationOptions();
      }
    } catch (error) {
      console.error("Error verifying team:", error);
      agentForm.team_id = undefined;
      agentForm.team_name = "";
      teamUsers.value = [];
    }
  } else {
    agentForm.team_name = "";
    teamUsers.value = [];
    // Clear location options when team is cleared
    locationOptions.value = [];
    agentForm.meeting_vendor = undefined;
  }
};

watch(
  () => agentForm.agent_name,
  (newValue) => {
    agentNameLength.value = newValue.length;
    if (newValue.length > 50 && !hasShownNameError.value) {
      message.error("Agent name cannot exceed 50 characters");
      hasShownNameError.value = true;
    } else if (newValue.length <= 50) {
      hasShownNameError.value = false;
    }
  },
);

const resetFormState = () => {
  const currentTimeSlots = agentForm.time_slots;
  const currentDuration = props.isEditing ? agentForm.duration : 30;
  const currentDurationOptions = props.isEditing ? [currentDuration] : [30];
  const currentWeightState = props.isEditing ? allowSetWeight.value : false;

  Object.assign(agentForm, createInitialFormState());

  if (props.isEditing) {
    agentForm.duration = currentDuration;
    agentForm.duration_options = currentDurationOptions;
  }

  if (Object.keys(currentTimeSlots).length > 0) {
    agentForm.time_slots = currentTimeSlots;
  }

  if (descriptionEditorRef.value) {
    descriptionEditorRef.value.quill?.setText("");
  }
  hasShownExceedMessage.value = false;
  enableBrandColor.value = false;

  advancedSettings.value = {
    limit_days_enabled: false,
    limit_days_config: null,
    buffer_time_enabled: false,
    buffer_time_config: null,
    minimum_notice_enabled: false,
    minimum_notice_config: null,
  };

  if (!props.isEditing || !currentWeightState) {
    allowSetWeight.value = false;
    agentForm.weights = {};
  } else {
    allowSetWeight.value = currentWeightState;
  }
};

const handleEditorUpdate = (value) => {
  const textStatus = checkTextLength(value, true);
  const previousLength = descriptionLength.value;
  descriptionLength.value = textStatus.plainTextLength;

  if (
    previousLength <= 400 &&
    textStatus.plainTextLength > 400 &&
    !hasShownExceedMessage.value
  ) {
    message.warning("Description exceeds 400 characters");
    hasShownExceedMessage.value = true;
  } else if (previousLength > 400 && textStatus.plainTextLength <= 400) {
    hasShownExceedMessage.value = false;
  }

  showDescriptionWarning.value = textStatus.isNearLimit;
  agentForm.description = value;
};

const sortedDurationOptions = computed({
  get: () => [...agentForm.duration_options].sort((a, b) => a - b),
  set: (value) => {
    agentForm.duration_options = [...value].sort((a, b) => a - b);
  },
});

const filteredDurationOptions = computed(() => {
  return durationOptions.filter((option) =>
    agentForm.duration_options.includes(option.value),
  );
});

watch(
  () => agentForm.duration_options,
  (newDurations) => {
    const sortedDurations = [...newDurations].sort((a, b) => a - b);
    if (!sortedDurations.includes(agentForm.duration)) {
      agentForm.duration = sortedDurations[0];
    }
  },
);

watch(allowCustomDuration, (newValue) => {
  if (!props.isEditing) {
    if (!newValue) {
      agentForm.duration_options = [30];
      agentForm.duration = 30;
    } else {
      agentForm.duration_options = [agentForm.duration];
    }
  } else {
    if (!newValue) {
      agentForm.duration_options = [agentForm.duration];
    }
  }
});

const toggleDayAvailability = (day, isAvailable) => {
  if (isAvailable) {
    agentForm.time_slots[day] = {
      slots: [{ start: "09:00am", end: "5:00pm" }],
    };
  } else {
    agentForm.time_slots[day] = { unavailable: true };
  }
};

const handleRemoveSlot = (day, index) => {
  agentForm.time_slots[day].slots.splice(index, 1);
  if (agentForm.time_slots[day].slots.length === 0) {
    agentForm.time_slots[day] = { unavailable: true };
  }
};

const handleAddSlot = (day) => {
  if (agentForm.time_slots[day].unavailable) {
    agentForm.time_slots[day] = { slots: [] };
  }
  agentForm.time_slots[day].slots.push({ start: "09:00am", end: "5:00pm" });
};

const onSubmit = async () => {
  if (!agentForm.meeting_vendor) {
    message.error("Location is required");
    return;
  }

  if (agentForm.agent_name.trim() === "") {
    websiteError.value = "Agent Name is required";
    message.error("Agent Name is required");
    return;
  }

  if (agentForm.agent_name.length > 50) {
    websiteError.value = "Agent name cannot exceed 50 characters";
    message.error("Agent name cannot exceed 50 characters");
    return;
  }

  const textStatus = checkTextLength(agentForm.description, true);

  if (textStatus.isExceeded) {
    message.error(
      `Description cannot exceed ${DESCRIPTION_LIMITS.PLAIN_TEXT} characters`,
    );
    return;
  }

  if (
    props.agentType === "Round Robin" &&
    (!agentForm.team_id || agentForm.team_id.length === 0)
  ) {
    message.error("Please select at least one team");
    return;
  }

  loading.value = true;
  try {
    let response;

    if (props.agentType === "Round Robin") {
      const verifyTeamId = {
        team_id: agentForm.team_id,
      };
      const teamValidation = await DashboardService.verifyTeam(verifyTeamId);
      if (!teamValidation?.success) {
        message.error(teamValidation?.message || "Invalid team selection");
        loading.value = false;
        return;
      }
    }

    const payload = {
      ...agentForm,
      agent_name: agentForm.agent_name,
      duration_options: agentForm.duration_options,
      description: agentForm.description,
      duration: agentForm.duration,
      theme: agentForm.theme,
      time_slots: convertTo24HourFormat(agentForm.time_slots),
      meeting_vendor: agentForm.meeting_vendor,
      digital_employee_id: agentForm.meeting_vendor === "ai_supermeet" ? agentForm.digital_employee_id : undefined,
      advance_settings: advancedSettings.value,
    };
    if (props.agentType === "Round Robin") {
      payload.team_id = agentForm.team_id;
      payload.type = "round_robin";
      payload.team_name = agentForm.team_name;
      payload.distribution = agentForm.distribution;
      payload.weight_distribution = allowSetWeight.value ? "custom" : "default";
      if (allowSetWeight.value && payload.weight_distribution === "custom") {
        payload.weights = agentForm.weights;
      } else {
        payload.weights = {};
      }
    }
    if (props.agentType === "One-on-One") {
      payload.time_slots = convertTo24HourFormat(agentForm.time_slots);
      payload.timezone = agentForm.timezone;
      payload.type = "one_on_one";
    }

    if (props.isEditing) {
      payload.id = props.agentId;
      response = await DashboardService.updateMeetingAgent(payload);
    } else {
      response = await DashboardService.createMeetingAgent(payload);
    }

    if (response.success) {
      message.success(
        `Agent ${props.isEditing ? "updated" : "created"} successfully`,
      );
      if (props.isEditing) {
        emit("agent-updated", response.agent);
      } else {
        emit("agent-created", response.agent);
      }
      emit("update:modelValue", false);
    } else {
      message.error(`Failed to ${props.isEditing ? "update" : "create"} agent`);
    }
  } catch (error) {
    console.error(
      `Error ${props.isEditing ? "updating" : "creating"} agent:`,
      error,
    );
    message.error(
      `An error occurred while ${
        props.isEditing ? "updating" : "creating"
      } the agent`,
    );
  } finally {
    loading.value = false;
  }
};

const fetchTeams = async () => {
  try {
    const response = await DashboardService.fetchTeams();
    if (response.success) {
      teamOptions.value = response.teams;
    }
  } catch (error) {
    console.error("Error fetching teams:", error);
    message.error("Failed to fetch teams");
  }
};

const fetchDigitalEmployees = async () => {
  digitalEmployeesLoading.value = true;
  try {
    const response = await DigitalEmployeeService.listEmployees(1, 250, { status: "active" });
    digitalEmployees.value = (response?.items || []).map((emp) => ({
      id: emp.id,
      name: emp.name,
    }));
  } catch (error) {
    console.error("Error fetching digital employees:", error);
  } finally {
    digitalEmployeesLoading.value = false;
  }
};

watch(
  () => agentForm.meeting_vendor,
  (newVal) => {
    if (newVal === "ai_supermeet") {
      if (digitalEmployees.value.length === 0) {
        fetchDigitalEmployees();
      }
    } else {
      agentForm.digital_employee_id = undefined;
    }
  },
);

const fetchAgentDetails = async (id) => {
  try {
    const response = await DashboardService.getMeetingAgentDetails(id);
    if (response.success && response.agent) {
      agentForm.duration_options = response.agent?.duration_options || [30];
      allowCustomDuration.value = response.agent?.duration_options?.length > 1;
      const currentDuration = response.agent?.duration || 30;
      agentForm.duration = currentDuration;
      if (response.agent?.duration_options?.length > 1) {
        agentForm.duration_options = response.agent.duration_options;
        allowCustomDuration.value = true;
      } else {
        agentForm.duration_options = [currentDuration];
        allowCustomDuration.value = false;
      }
      if (
        response.agent.advance_settings &&
        (response.agent.advance_settings.limit_days_enabled ||
          response.agent.advance_settings.buffer_time_enabled ||
          response.agent.advance_settings.minimum_notice_enabled)
      ) {
        advancedSettings.value = response.agent.advance_settings;
      } else {
        advancedSettings.value = {
          limit_days_enabled: false,
          limit_days_config: null,
          buffer_time_enabled: false,
          buffer_time_config: null,
          minimum_notice_enabled: false,
          minimum_notice_config: null,
        };
      }

      const storedWeights = response.agent?.weights || {};
      const storedWeightDistribution =
        response.agent?.weight_distribution || "default";

      Object.assign(agentForm, {
        id: response.agent?.id,
        agent_name: response.agent?.name,
        description: response.agent?.description || "",
        time_slots: convertTo12HourFormat(response.agent?.time_slots),
        timezone: response.agent?.owner_timezone,
        theme: response.agent?.theme || "light",
        team_id: response.agent?.team_id,
        meeting_vendor: response.agent?.meeting_vendor,
        brand_color: response.agent?.brand_color || "",
        distribution: response.agent?.distribution || "flexible",
        digital_employee_id: response.agent?.digital_employee_id || undefined,
      });

      if (response.agent?.team_id) {
        const verifyTeamId = {
          team_id: response.agent.team_id,
        };
        const teamResponse = await DashboardService.verifyTeam(verifyTeamId);

        if (teamResponse?.team_users) {
          teamUsers.value = teamResponse.team_users.map((user) => ({
            ...user,
            weight: storedWeights[user.id] || 1,
          }));
          agentForm.team_name = teamResponse.team_name || "";
          agentForm.weights = storedWeights;
          agentForm.weight_distribution = storedWeightDistribution;
          allowSetWeight.value = storedWeightDistribution === "custom";
        }

        if (props.agentType === "Round Robin") {
          fetchLocationOptions();
        }
      }
      enableBrandColor.value = !!response.agent?.brand_color;
    } else {
      message.error("Failed to fetch agent details");
    }
  } catch (error) {
    console.error("Error fetching agent:", error);
    message.error("An error occurred while fetching agent details");
  }
};

const fetchLocationOptions = async () => {
  try {
    // For Round Robin, only fetch vendors if team is selected
    if (props.agentType === "Round Robin" && !agentForm.team_id) {
      locationOptions.value = [];
      return;
    }

    const payload = agentForm.team_id ? { team_id: agentForm.team_id } : {};
    const response = await DashboardService.fetchLocationOptionsMeeting(payload);

    const buildOption = (vendorValue, { disabled = false } = {}) => {
      const option = locationOptionsMap[vendorValue];
      return {
        value: vendorValue,
        label: option?.label || vendorValue,
        icon: option?.icon || imageNames.SUPER_MEET,
        disabled,
        ...(disabled && { tooltip: "Teams for Business is required to schedule meetings" }),
      };
    };

    // Handle case where default vendor is not verified - don't show error, just don't set default
    if (response.success && response.valid_team === false) {
      agentForm.meeting_vendor = undefined;

      // Still populate the options but don't set default
      if (response.vendors) {
        locationOptions.value = response.vendors.map((v) => buildOption(v));
      }

      // Append blocked vendors (e.g. ms_teams on non-business Outlook)
      (response.blocked_vendors || []).forEach((v) => {
        locationOptions.value.push(buildOption(v, { disabled: true }));
      });
      return;
    }

    if (response.success && response.response) {
      const availableVendors = response.response;
      locationOptions.value = availableVendors.map((v) => buildOption(v));

      // Append blocked vendors (e.g. ms_teams on non-business Outlook)
      (response.blocked_vendors || []).forEach((v) => {
        locationOptions.value.push(buildOption(v, { disabled: true }));
      });

      if (!props.isEditing && availableVendors.length > 0) {
        if (props.agentType == "Round Robin" || props.agentType == "One-on-One" ) {
          agentForm.meeting_vendor = availableVendors[0];
        } else {
          agentForm.meeting_vendor = "super_meet";
        }
      }
    } else {
      console.warn("Invalid response format:", response);
    }
  } catch (error) {
    console.error("Error fetching location options:", error);
    message.error("Failed to fetch location options");
  }
};

const fetchUserAvailability = async () => {
  try {
    const data = await DashboardService.getUserAvailability();

    if (data?.availability) {
      const normalizedTimeSlots = {};

      Object.entries(data.availability).forEach(([day, dayData]) => {
        if (dayData.unavailable) {
          normalizedTimeSlots[day] = { unavailable: true };
        } else if (dayData.slots) {
          normalizedTimeSlots[day] = {
            slots: dayData.slots.map((slot) => ({
              start: convertTo12Hour(slot.start.split("+")[0]),
              end: convertTo12Hour(slot.end.split("+")[0]),
            })),
          };
        }
      });

      if (props.agentType === "One-on-One") {
        agentForm.time_slots = normalizedTimeSlots;
      }
    }

    if (data?.timezone) {
      agentForm.timezone = data.timezone;
    }
  } catch (error) {
    console.error("Error fetching availability:", error);
    message.error("Failed to fetch default availability");
  }
};

const handleLocationSelect = async (value) => {
  if (props.agentType === "Round Robin" && !agentForm.team_id) {
    if (!agentForm.team_id) {
      message.error("Please select a team before choosing location");
      setTimeout(() => {
        agentForm.meeting_vendor = undefined;
      });
      return;
    }
  }

  if (props.agentType === "Round Robin") {
    try {
      const verifyLocationPayload = {
        meeting_vendor: value,
        team_id: agentForm.team_id,
      };

      const response = await DashboardService.verifyLocation(
        verifyLocationPayload,
      );

      if (response.success && !response.valid_team) {
        message.error(
          response.error || "Some team members don't have MS Teams configured",
        );
        agentForm.meeting_vendor = undefined;
        return;
      }

      if (!response.success) {
        message.error(response.message || "Failed to verify location");
        agentForm.meeting_vendor = undefined;
      }
    } catch (error) {
      console.error("Error verifying location:", error);
      message.error("Failed to verify location");
      agentForm.meeting_vendor = undefined;
    }
  }
};

const isLowContrast = computed(() => {
  if (!agentForm.brand_color) return false;

  try {
    const hex = agentForm.brand_color.replace("#", "");
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    if (isNaN(r) || isNaN(g) || isNaN(b)) return false;

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return agentForm.theme === "dark" ? luminance < 0.3 : luminance > 0.7;
  } catch (e) {
    console.error("Error calculating contrast:", e);
    return false;
  }
});

onMounted(() => {
  fetchTeams();
  fetchUserAvailability();
});

watch(
  () => props.drawerVisible,
  (newVal) => {
    internalDrawerVisible.value = newVal;
    if (newVal && props.isEditing) {
      fetchAgentDetails(props.agentId);
    }
  },
  { immediate: true },
);

watch(
  () => internalDrawerVisible.value,
  (newVal) => {
    if (!newVal) {
      emit("update:modelValue", false);
      resetFormState();
      allowCustomDuration.value = false;
    } else {
      fetchLocationOptions();
      if (props.isEditing && props.agentId) {
        fetchAgentDetails(props.agentId);
      } else {
        advancedSettings.value = null;
        if (props.agentType === "One-on-One") {
          fetchUserAvailability();
        } else if (props.agentType === "Round Robin") {
          agentForm.meeting_vendor = undefined;
        }
      }
    }
  },
);

watch(allowSetWeight, (newValue) => {
  if (
    newValue &&
    (!agentForm.team_id || !teamUsers.value.length) &&
    !props.isEditing
  ) {
    message.error("Please select a team before enabling Set Weight");
    allowSetWeight.value = false;
    return;
  }

  agentForm.weight_distribution = newValue ? "custom" : "default";
  if (!newValue) {
    agentForm.weights = {};
    if (teamUsers.value.length) {
      teamUsers.value = teamUsers.value.map((user) => ({
        ...user,
        weight: 1,
      }));
    }
  } else {
    agentForm.weights = teamUsers.value.reduce((acc, user) => {
      acc[user.id] = 1;
      return acc;
    }, {});
  }
});
</script>
<style scoped>
:deep(.ant-select-selector) {
  cursor: pointer !important;
}

:deep(.ant-select-dropdown)
  .ant-select-item-option-content
  .distribution-label {
  font-weight: 600;
  font-size: 14px;
}

:deep(.ant-select-dropdown) .ant-select-item-option-content .distribution-desc {
  font-size: 13px;
}

:deep(.ant-select-selection-item) .distribution-desc {
  display: none;
}

:deep(.ant-select-selection-item) .distribution-label {
  font-weight: normal;
  font-size: inherit;
}

:deep(.ant-checkbox) {
  margin-right: 8px;
}

:deep(.ant-checkbox-inner) {
  background-color: transparent;
  border: 1px solid var(--white-opacity-8);
}

:deep(.ant-checkbox-checked .ant-checkbox-inner) {
  background-color: #4b5563;
  border: 1px solid var(--white-opacity-8);
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

:deep(.ant-select-item-option-disabled) {
  pointer-events: all !important;
  cursor: not-allowed !important;
}

.team-box {
  border: 1px solid var(--white-opacity-8);
  padding: 8px;
}
.teams-container {
  border: 1px solid var(--white-opacity-8);
  background-color: rgba(255, 255, 255, 0.04);
}
</style>
